import { useState, useEffect, useRef } from 'react';
import { doc, getDoc, onSnapshot, runTransaction, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getClientDb } from '../firebase/clientApp';
import { Modal, ModalBanner, Button, FormField } from './ui';
import { formatAddressLines, legacyToBillTo } from '../utils/address';
import { useAuth } from '../context/AuthContext';
import {
  paginateRows, numberToWords,
  LOGO_W_DEFAULT, LOGO_W_MIN, LOGO_W_MAX, LOGO_RATIO,
  FOOTER_IMG_H_DEFAULT, FOOTER_IMG_H_MIN, FOOTER_IMG_H_MAX,
} from '../utils/invoiceSheets';
import { fmtMoney } from '../utils/currency';
import { entryId } from '../utils/ledger';
import type { CustomerMemo, Entity, BusinessSettings, InventoryMemoStatus, EntityAddress } from '../types';

// ── Invoice Options — same shape/behavior as Sales.tsx's own invoiceOptions
// panel (accent color, page-layout toggles, trade-detail fields, customer
// memo), reused here per explicit ask that the consignment document work
// "always" the same way the Sale invoice does. hidePaymentInfo has no
// equivalent here — this document has no payment/bank section to hide. ──
const INVOICE_ACCENT_SWATCHES = ['#0F172A', '#DB2777', '#EA580C', '#1E3A8A', '#2563EB', '#0EA5E9', '#059669', '#6B7280'];
const INVOICE_OPTION_TOGGLES: { key: keyof NonNullable<CustomerMemo['invoiceOptions']>; label: string }[] = [
  { key: 'hideTotal',           label: 'Hide the total' },
  { key: 'hideItemPrice',       label: 'Hide item price (Price/ct)' },
  { key: 'hideCustomerCountry', label: 'Hide customer country' },
  { key: 'showCustomerPhone',   label: "Show customer's phone number" },
  { key: 'showCustomerEmail',   label: "Show customer's email" },
];
const INVOICE_TEXT_FIELDS: { key: keyof NonNullable<CustomerMemo['invoiceOptions']>; label: string }[] = [
  { key: 'terms',      label: 'Terms' },
  { key: 'shipVia',    label: 'Ship Via' },
  { key: 'insuredBy',  label: 'Insured By' },
  { key: 'salesman',   label: 'Salesman' },
  { key: 'cifDetails', label: 'CIF Details' },
  { key: 'origin',      label: 'Origin' },
  { key: 'roughOrigin', label: 'Origin of Rough' },
];

function IconChevronDown() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M3 5.5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
// Same three icons/glyphs as Sales.tsx's InvoicePreviewModal action trio —
// duplicated per this app's per-page-helper convention, so the Consignment
// preview's Mark as Sent / PDF / Print buttons look identical to the Sale
// invoice's.
function IconPrint() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M4.5 6V2h7v4M3 6.5h10a1.5 1.5 0 0 1 1.5 1.5v4H12v2H4v-2H1.5V8A1.5 1.5 0 0 1 3 6.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M4 10.5h8" stroke="currentColor" strokeWidth="1.3" />
  </svg>;
}
function IconPdf() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M4 1.5h5.5L12.5 4.5V13.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2.5a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M9.5 1.5V4.5H12.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M4.8 11.5V8.2h.9c.55 0 1 .4 1 1v.3c0 .6-.45 1-1 1h-.9M7.8 11.5V8.2h1c.6 0 1.05.45 1.05 1.05v1.2c0 .6-.45 1.05-1.05 1.05h-1ZM11 11.5V8.2h1.4M11 9.9h1.2"
      stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCheck() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 7.2l3 3 6-6.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
// Collapsible section for the Options panel — same plain conditional-render
// component Sales.tsx uses (no animation), duplicated per this app's
// per-page-helper convention.
function OptionsSection({ title, defaultOpen, children }: {
  title: string; defaultOpen?: boolean; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div style={{ marginBottom: 18 }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em',
      }} aria-expanded={open}>
        {title}
        <span style={{
          display: 'flex', color: 'var(--text-3)',
          transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 0.18s ease',
        }}>
          <IconChevronDown />
        </span>
      </button>
      {open && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  );
}

function LotPill({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', fontFamily: 'var(--font-num)', background: 'var(--primary-light)',
      color: 'var(--primary)', fontSize: 11, fontWeight: 700, padding: '3px 8px',
      borderRadius: 6, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}
// Same convention as every other document number in this app (last 4 digits
// only, full value in a title tooltip next to it) — see Sales.tsx's own
// lotNoDisplay for the identical pattern.
function memoNoDisplay(memoNo?: string) {
  if (!memoNo) return '—';
  const m = memoNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : memoNo.slice(-4);
}
function fmtShortDate(dateStr?: string) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Printable consignment document: the same A4 sheet layout as the Sales
// invoice (Sales.tsx InvoiceDocument). Rows are split over real A4 sheets
// (paginateRows on a hidden measuring copy); every sheet repeats the logo,
// company, consignment facts (+ "Page N of M"), Bill To / Ship To, the 4 × 2
// trade details grid and the table head. Only the last sheet has the Total
// row, the amount in words + the "not a sales invoice" line, and the footer
// (customer memo + Settings' footer note left, footer image right) pinned
// to its bottom edge. No bank details: nothing is due on a consignment.
// Used by Inventory.tsx (right after a bulk Send On Memo) and Memos.tsx.
export function ConsignmentDocument({ memo, entity, settings, business }: {
  memo: CustomerMemo;
  entity: Entity | undefined;
  settings: BusinessSettings | null;
  business: { businessName: string } | null;
}) {
  const companyName = settings?.invoiceCompanyName || business?.businessName || '—';
  // Pincode, City and Country share one line: "2018 Antwerp, Belgium".
  const companyAddressLines = [
    settings?.invoiceAddressLine1,
    settings?.invoiceAddressLine2,
    [
      [settings?.invoicePostalCode, settings?.invoiceCity].filter(Boolean).join(' '),
      settings?.invoiceCountry,
    ].filter(Boolean).join(', '),
  ].filter((l): l is string => !!l && l.trim().length > 0);
  const opts = memo.invoiceOptions ?? {};
  const accent = opts.accentColor || '#0F172A';
  // "Hide customer country" drops the country line from both addresses.
  const withoutCountry = (a: EntityAddress | undefined | null) => {
    const lines = formatAddressLines(a);
    return opts.hideCustomerCountry && a?.country ? lines.filter(l => l !== a.country!.trim()) : lines;
  };
  const billTo = entity?.billTo ?? legacyToBillTo(entity?.address);
  const billLines = withoutCountry(billTo);
  // Ship To always renders as its own box, even when it's the same as Bill To.
  const shipTo = entity?.shipToSameAsBillTo ? billTo : (entity?.shipTo ?? billTo);
  const shipLines = withoutCountry(shipTo);
  const money = (v: number) => fmtMoney(v ?? 0, settings?.currency ?? 'USD');
  const showTotals = !opts.hideTotal;
  const avgPricePerCt = memo.totalCarat > 0 ? memo.totalAmountUSD / memo.totalCarat : 0;
  const descStyle = opts.hideItemPrice ? { width: '59%' } : undefined;
  const lines = memo.lines;

  const note = memo.notes?.trim();
  const footerNote = opts.hideFooterNote ? undefined : settings?.invoiceFooterNote?.trim();
  const footerImg = opts.hideFooterImage ? undefined : settings?.invoiceRjcCertDataUrl;
  const footerImgH = settings?.invoiceFooterImageHeight ?? FOOTER_IMG_H_DEFAULT;
  const hasFooter = !!(note || footerNote || footerImg);
  const logoW = settings?.invoiceLogoWidth ?? LOGO_W_DEFAULT;

  // ── Pagination (same as the Sales invoice) ─────────────────────────────
  const measureRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<{ sig: string; pages: number[][] } | null>(null);
  const sig = `${memo.id}:${lines.length}`;
  useEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const next = paginateRows(el, lines.length);
      if (!next) return;
      setPages(prev => (prev && prev.sig === sig && JSON.stringify(prev.pages) === JSON.stringify(next)) ? prev : { sig, pages: next });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [sig, lines.length]);
  const sheets: number[][] = pages?.sig === sig ? pages.pages : [lines.map((_, i) => i)];
  const pageCount = sheets.length;
  const lastTableSheet = sheets.reduce((acc, idxs, i) => idxs.length > 0 ? i : acc, 0);

  const top = (pageNo: number, count: number) => (
    <div data-m="top" style={{ display: 'flow-root' }}>
      <div className="invoice-doc-header">
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          {settings?.invoiceLogoDataUrl && (
            <img src={settings.invoiceLogoDataUrl} alt="" className="invoice-doc-logo"
              style={{ width: logoW, height: Math.round(logoW * LOGO_RATIO) }} />
          )}
          <div>
            <div className="invoice-doc-company">{companyName}</div>
            {companyAddressLines.map((l, i) => <div key={i} className="invoice-doc-meta-line">{l}</div>)}
            {settings?.invoiceVatNumber && <div className="invoice-doc-meta-line">VAT: {settings.invoiceVatNumber}</div>}
            {(settings?.invoiceEmail || settings?.invoicePhone) && (
              <div className="invoice-doc-meta-line">
                {[settings?.invoiceEmail, settings?.invoicePhone].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        </div>
        <div className="invoice-doc-facts">
          <div className="invoice-doc-eyebrow">Consignment</div>
          <div className="invoice-doc-fact-row">Memo No.<strong title={memo.memoNo}>{memoNoDisplay(memo.memoNo)}</strong></div>
          <div className="invoice-doc-fact-row">Date<strong>{fmtShortDate(memo.date)}</strong></div>
          {count > 1 && <div className="invoice-doc-fact-row">Page<strong>{pageNo} of {count}</strong></div>}
        </div>
      </div>

      <div className="invoice-doc-address-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div>
          <div className="invoice-doc-section-label">Bill To</div>
          <div className="invoice-doc-billto">
            <div style={{ fontWeight: 700, color: '#0F172A' }}>{entity?.companyName ?? '—'}</div>
            {billLines.map((l, i) => <div key={i}>{l}</div>)}
            {entity?.vatGstBtw && <div>VAT: {entity.vatGstBtw}</div>}
            {opts.showCustomerPhone && entity?.phone && <div>{entity.phone}</div>}
            {opts.showCustomerEmail && entity?.email && <div>{entity.email}</div>}
          </div>
        </div>
        <div>
          <div className="invoice-doc-section-label">Ship To</div>
          <div className="invoice-doc-billto">
            <div style={{ fontWeight: 700, color: '#0F172A' }}>{shipTo?.name || entity?.companyName || '—'}</div>
            {shipLines.map((l, i) => <div key={i}>{l}</div>)}
            {entity?.vatGstBtw && <div>VAT: {entity.vatGstBtw}</div>}
            {opts.showCustomerPhone && entity?.phone && <div>{entity.phone}</div>}
            {opts.showCustomerEmail && entity?.email && <div>{entity.email}</div>}
          </div>
        </div>
      </div>

      {/* Trade details: the Sales invoice's 4 × 2 grid, Return By in the
          Due Date slot (Terms here is free text). */}
      <div className="invoice-doc-terms-grid">
        {([
          ['Terms',           opts.terms],
          ['Ship Via',        opts.shipVia],
          ['Insured By',      opts.insuredBy],
          ['Origin',          opts.origin],
          ['Return By',       fmtShortDate(memo.returnByDate)],
          ['Salesman',        opts.salesman],
          ['CIF Details',     opts.cifDetails],
          ['Origin of Rough', opts.roughOrigin],
        ] as [string, string | undefined][]).map(([label, value]) => (
          <div key={label} className="invoice-doc-terms-cell">
            <div className="invoice-doc-payment-label">{label}</div>
            <div className="invoice-doc-payment-value">{value || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const thead = (
    <thead data-m="thead">
      <tr>
        <th className="sr">Sr.</th>
        <th className="desc" style={descStyle}>Description</th>
        <th className="num carat">Carat</th>
        {!opts.hideItemPrice && <th className="num price">Rate/ct</th>}
        <th className="num amount">Amount</th>
      </tr>
    </thead>
  );

  const renderRow = (i: number, measure: boolean) => {
    const li = lines[i];
    return (
      <tr key={li.id} data-m-row={measure ? '' : undefined}>
        <td className="sr">{i + 1}</td>
        <td className="desc" style={descStyle}>
          {li.description || <>{li.code ? `${li.code} · ` : ''}{li.shape} {li.color}/{li.clarity}</>}
        </td>
        <td className="num carat">{li.carat.toFixed(2)}</td>
        {!opts.hideItemPrice && <td className="num price">{li.pricePerCt ? money(li.pricePerCt) : <span style={{ opacity: 0.4 }}>—</span>}</td>}
        <td className="num amount">{li.amountUSD ? money(li.amountUSD) : <span style={{ opacity: 0.4 }}>—</span>}</td>
      </tr>
    );
  };

  const tfoot = showTotals && (
    <tfoot data-m="tfoot">
      <tr>
        <td colSpan={2} className="invoice-doc-table-total-label">
          <div style={{ display: 'flex', alignItems: 'center' }}><span style={{ marginLeft: 'auto' }}>Total</span></div>
        </td>
        <td className="num carat invoice-doc-table-total-label">{memo.totalCarat.toFixed(2)}</td>
        {!opts.hideItemPrice && <td className="num price invoice-doc-table-total-label">{money(avgPricePerCt)}</td>}
        <td className="num amount invoice-doc-table-total-label">{money(memo.totalAmountUSD)}</td>
      </tr>
    </tfoot>
  );

  // The value in words (as on the invoice), then the "still ours" line.
  const end = (
    <div data-m="end" style={{ display: 'flow-root' }}>
      <div className="invoice-doc-bank">
        {showTotals && (
          <div className="invoice-doc-bank-value" style={{ fontStyle: 'italic' }}>
            {numberToWords(memo.totalAmountUSD)} Only
          </div>
        )}
        <div className="invoice-doc-bank-value" style={{ marginTop: showTotals ? 4 : 0 }}>
          These goods remain the property of {companyName} until purchased. This is not a sales invoice.
        </div>
      </div>
    </div>
  );

  const footer = hasFooter && (
    <div data-m="footer" className="invoice-doc-footer-row">
      <div className="invoice-doc-footer-note">
        {note && <div className="invoice-doc-memo">{note}</div>}
        {footerNote && <div className="invoice-doc-settings-note" style={note ? { marginTop: 6 } : undefined}>{footerNote}</div>}
      </div>
      {footerImg && <div className="invoice-doc-footer-img-box"><img src={footerImg} alt="" className="invoice-doc-footer-img" style={{ maxHeight: footerImgH }} /></div>}
    </div>
  );

  const docStyle = { '--invoice-accent': accent } as React.CSSProperties;

  return (
    <>
      {sheets.map((idxs, p) => {
        const isLast = p === pageCount - 1;
        return (
          <div key={p} className="invoice-doc invoice-doc-sheet" style={docStyle}>
            {top(p + 1, pageCount)}
            {idxs.length > 0 && (
              <table className="invoice-doc-table">
                {thead}
                <tbody>{idxs.map(i => renderRow(i, false))}</tbody>
                {p === lastTableSheet && tfoot}
              </table>
            )}
            {isLast && end}
            {isLast && footer}
          </div>
        );
      })}

      {/* Hidden full-length copy, only used to measure row heights. */}
      <div ref={measureRef} className="invoice-doc invoice-doc-measure" style={docStyle} aria-hidden>
        {top(1, 2)}
        <table className="invoice-doc-table">
          {thead}
          <tbody>{lines.map((_, i) => renderRow(i, true))}</tbody>
          {tfoot}
        </table>
        {end}
        {footer}
      </div>
    </>
  );
}

// ── Detail + print modal — the single entry point both Inventory.tsx (right
// after a bulk send) and Memos.tsx's Consignments tab open. Self-contained:
// fetches each line's LIVE InventoryMemo status on open (display only, see
// CustomerMemo's own "point-in-time snapshot" doc comment) and owns the
// manual Close Consignment transaction itself, so neither host page needs
// to duplicate that logic.
export function ConsignmentPreviewModal({
  memo: memoProp, entity, settings, business, open, onClose,
}: {
  memo: CustomerMemo | null;
  entity: Entity | undefined;
  settings: BusinessSettings | null;
  business: { businessName: string } | null;
  open: boolean;
  onClose: () => void;
}) {
  // Follow the saved memo live: the popup opened right after Send On Memo
  // got a one time copy, so ticking an option (Show footer image…) saved
  // but the sheet never changed.
  const [live, setLive] = useState<CustomerMemo | null>(null);
  const memoId = memoProp?.id;
  useEffect(() => {
    const db = getClientDb();
    if (!db || !memoId || !open) return;
    return onSnapshot(doc(db, 'customerMemos', memoId), snap => {
      if (snap.exists()) setLive({ id: snap.id, ...snap.data() } as CustomerMemo);
    }, () => { /* keep the copy we were given */ });
  }, [memoId, open]);
  const memo = live && live.id === memoId ? live : memoProp;
  const [lineStatuses, setLineStatuses] = useState<Record<string, InventoryMemoStatus> | null>(null);
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const docWrapRef = useRef<HTMLDivElement>(null);
  const { business: authBusiness } = useAuth();
  // Logo Size / Footer Image sliders: the sheet follows while dragging and
  // the size is saved once for the business 400ms after the slider stops
  // (the same setting the Sales invoice uses).
  const [logoDraft, setLogoDraft] = useState<number | null>(null);
  const [footImgDraft, setFootImgDraft] = useState<number | null>(null);
  useEffect(() => {
    if (logoDraft === null || !authBusiness) return;
    if (logoDraft === (settings?.invoiceLogoWidth ?? LOGO_W_DEFAULT)) return;
    const t = setTimeout(async () => {
      const db = getClientDb();
      if (!db) return;
      try {
        await setDoc(doc(db, 'settings', authBusiness.id), { invoiceLogoWidth: logoDraft, updatedAt: serverTimestamp() }, { merge: true });
        flashSaved();
      } catch { /* the slider keeps its value; the next move retries */ }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoDraft]);
  useEffect(() => {
    if (footImgDraft === null || !authBusiness) return;
    if (footImgDraft === (settings?.invoiceFooterImageHeight ?? FOOTER_IMG_H_DEFAULT)) return;
    const t = setTimeout(async () => {
      const db = getClientDb();
      if (!db) return;
      try {
        await setDoc(doc(db, 'settings', authBusiness.id), { invoiceFooterImageHeight: footImgDraft, updatedAt: serverTimestamp() }, { merge: true });
        flashSaved();
      } catch { /* the slider keeps its value; the next move retries */ }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footImgDraft]);

  useEffect(() => {
    setDraftNotes(memo?.notes ?? '');
  }, [memo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function flashSaved() {
    setSavingEdit(true);
    setTimeout(() => setSavingEdit(false), 1200);
  }

  // Same "write one invoiceOptions key" pattern as Sales.tsx's
  // updateInvoiceOption — this document/panel is meant to behave
  // identically to the Sale invoice's.
  async function updateMemoOption(
    memoId: string,
    key: keyof NonNullable<CustomerMemo['invoiceOptions']>,
    value: boolean | string,
  ) {
    const db = getClientDb();
    if (!db) return;
    try {
      await updateDoc(doc(db, 'customerMemos', memoId), {
        [`invoiceOptions.${key}`]: value,
        updatedAt: serverTimestamp(),
      });
      flashSaved();
    } catch { /* the live listener just keeps showing the pre-toggle state */ }
  }
  async function saveMemoNotes(memoId: string) {
    const db = getClientDb();
    if (!db) return;
    try {
      await updateDoc(doc(db, 'customerMemos', memoId), {
        notes: draftNotes.trim() || null,
        updatedAt: serverTimestamp(),
      });
      flashSaved();
    } catch { /* the field keeps the unsaved draft so nothing is lost */ }
  }

  // Same toggle as Sales.tsx's toggleSent, mirrored onto CustomerMemo's own
  // invoiceSentStatus/invoiceSentAt fields.
  async function toggleSent(m: CustomerMemo) {
    const db = getClientDb();
    if (!db) return;
    const nextSent = m.invoiceSentStatus !== 'sent';
    try {
      await updateDoc(doc(db, 'customerMemos', m.id), {
        invoiceSentStatus: nextSent ? 'sent' : 'not-sent',
        invoiceSentAt: nextSent ? new Date().toISOString() : null,
        updatedAt: serverTimestamp(),
      });
    } catch { /* transient — the live listener will just show the pre-toggle state again */ }
  }

  // One A4 PDF page per sheet, the same as the Sales invoice's PDF.
  async function handleDownloadPdf() {
    const sheets = Array.from(docWrapRef.current?.querySelectorAll<HTMLElement>('.invoice-doc-sheet') ?? []);
    if (sheets.length === 0 || !memo) return;
    setDownloadingPdf(true);
    try {
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
      const pageWidth  = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      for (let i = 0; i < sheets.length; i++) {
        const canvas = await html2canvas(sheets[i], { scale: 3, useCORS: true, backgroundColor: '#ffffff' });
        if (i > 0) pdf.addPage();
        const imgHeight = Math.min(pageHeight, (canvas.height * pageWidth) / canvas.width);
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pageWidth, imgHeight);
      }
      pdf.save(`Consignment-${memoNoDisplay(memo.memoNo)}.pdf`);
    } catch { /* best-effort — the Print button still works as a fallback */ }
    setDownloadingPdf(false);
  }

  useEffect(() => {
    if (!open || !memo) { setLineStatuses(null); return; }
    setLineStatuses(null);
    setCloseError('');
    const db = getClientDb();
    if (!db) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(memo.lines.map(async li => {
        try {
          const snap = await getDoc(doc(db, 'inventory', li.inventoryItemId, 'memos', li.inventoryMemoId));
          const status = snap.exists() ? ((snap.data().status as InventoryMemoStatus) ?? 'active') : 'returned';
          return [li.id, status] as const;
        } catch {
          return [li.id, 'active'] as const; // fail closed — never let an unreadable line look resolved
        }
      }));
      if (!cancelled) setLineStatuses(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [open, memo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same class-toggle print recipe Sales.tsx's handlePrintInvoice already
  // established — reused verbatim (not a parallel `printing-consignment`
  // class) since this document uses the identical .invoice-doc-table
  // column structure the existing print CSS already targets.
  function handlePrint() {
    document.body.classList.add('printing-invoice');
    const after = () => {
      document.body.classList.remove('printing-invoice');
      window.removeEventListener('afterprint', after);
    };
    window.addEventListener('afterprint', after);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.print());
    });
  }

  const stillOut = !lineStatuses || Object.values(lineStatuses).some(s => s === 'active');

  async function handleCloseConsignment() {
    if (!memo) return;
    if (stillOut) {
      setCloseError('Some parcels are still out (or partially out) — return or sell them first.');
      return;
    }
    setClosing(true); setCloseError('');
    const db = getClientDb();
    if (!db) { setCloseError('Database not connected.'); setClosing(false); return; }
    try {
      await runTransaction(db, async tx => {
        tx.update(doc(db, 'customerMemos', memo.id), {
          status: 'closed', closedAt: new Date().toISOString(),
        });
        tx.delete(doc(db, 'journalEntries', entryId('memo_out', memo.id)));
      });
      onClose();
    } catch (e) {
      setCloseError(e instanceof Error ? e.message : 'Failed to close consignment.');
    }
    setClosing(false);
  }

  if (!memo) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      hideHead
      size="2xl"
    >
      <ModalBanner
        title={entity?.companyName || memoNoDisplay(memo.memoNo)}
        eyebrow="Consignment"
        noGradient
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {savingEdit && <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 600 }}>✓ Saved</span>}
            <span title={memo.memoNo}><LotPill>{memoNoDisplay(memo.memoNo)}</LotPill></span>
            {memo.status === 'open' && (
              <Button variant="secondary" size="sm" onClick={handleCloseConsignment} loading={closing} disabled={lineStatuses === null || stillOut}>
                Close Consignment
              </Button>
            )}
            {/* Same grouped action trio as Sales.tsx's Sale Invoice view —
                added per explicit ask that Memo Out get "this buttons" too. */}
            <div className="header-action-bar">
              <button
                type="button"
                className={memo.invoiceSentStatus === 'sent' ? 'active' : undefined}
                onClick={() => toggleSent(memo)}
                title={memo.invoiceSentStatus === 'sent' ? 'Click to mark Not Sent' : 'Click to mark Sent'}
              >
                <IconCheck />
                <span>{memo.invoiceSentStatus === 'sent' ? 'Sent' : 'Mark as Sent'}</span>
              </button>
              <button type="button" onClick={handleDownloadPdf} disabled={downloadingPdf} title="Download PDF">
                <IconPdf />
                <span>{downloadingPdf ? 'Generating…' : 'PDF'}</span>
              </button>
              <button type="button" onClick={handlePrint} title="Print">
                <IconPrint />
                <span>Print</span>
              </button>
            </div>
          </div>
        }
        onClose={onClose}
      />

      {closeError && <div className="msg msg-error">{closeError}</div>}

      {/* Document (left) + Invoice Options (right) — same side-by-side,
          sticky-panel layout as Sales.tsx's own viewingInvoice modal, per
          the explicit ask that this "always" work the same way. */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div ref={docWrapRef} style={{ flex: '1 1 700px', minWidth: 0, position: 'relative' }}>
          <ConsignmentDocument
            memo={memo} entity={entity} business={business}
            settings={settings && (logoDraft !== null || footImgDraft !== null) ? {
              ...settings,
              ...(logoDraft !== null ? { invoiceLogoWidth: logoDraft } : {}),
              ...(footImgDraft !== null ? { invoiceFooterImageHeight: footImgDraft } : {}),
            } : settings}
          />
        </div>

        {/* top: 60, not -20 — see the identical comment on Sales.tsx's own
            Invoice Options panel: -20 is ModalBanner's own flush-to-edge
            offset, and reusing it here made this panel stick at the same
            scroll position as the banner and disappear behind it. */}
        <div className="no-print card" style={{ flex: '0 0 320px', width: 320, position: 'sticky', top: 60 }}>
          <div className="card-title">Invoice Options</div>

          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 8 }}>
              Accent Color
            </label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {INVOICE_ACCENT_SWATCHES.map(c => {
                const active = (memo.invoiceOptions?.accentColor || '#0F172A') === c;
                return (
                  <button
                    key={c}
                    type="button"
                    title={c}
                    onClick={() => updateMemoOption(memo.id, 'accentColor', c)}
                    style={{
                      width: 22, height: 22, borderRadius: '50%', background: c, padding: 0, cursor: 'pointer',
                      border: active ? '2px solid var(--text-1)' : '2px solid var(--surface)',
                      boxShadow: '0 0 0 1px var(--border)',
                    }}
                  />
                );
              })}
            </div>
          </div>

          <OptionsSection title="Page Layout">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {INVOICE_OPTION_TOGGLES.map(o => (
                <label key={o.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-1)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!memo.invoiceOptions?.[o.key]}
                    onChange={e => updateMemoOption(memo.id, o.key, e.target.checked)}
                    style={{ accentColor: 'var(--primary)', flexShrink: 0 }}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </OptionsSection>

          {settings?.invoiceLogoDataUrl && (() => {
            const w = logoDraft ?? settings.invoiceLogoWidth ?? LOGO_W_DEFAULT;
            return (
              <OptionsSection title="Logo Size" defaultOpen>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Small</span>
                  <input
                    type="range" min={LOGO_W_MIN} max={LOGO_W_MAX} step={2} value={w}
                    onChange={e => setLogoDraft(Number(e.target.value))}
                    aria-label="Logo size"
                    style={{ flex: 1, accentColor: 'var(--primary)', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Big</span>
                </div>
                {w !== LOGO_W_DEFAULT && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                    <button type="button" className="line-item-link-btn" style={{ fontSize: 11.5 }}
                      onClick={() => setLogoDraft(LOGO_W_DEFAULT)}>Reset</button>
                  </div>
                )}
              </OptionsSection>
            );
          })()}

          {settings?.invoiceFooterNote?.trim() && (
            <OptionsSection title="Footer Note" defaultOpen>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-1)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!memo.invoiceOptions?.hideFooterNote}
                  onChange={e => updateMemoOption(memo.id, 'hideFooterNote', !e.target.checked)}
                  style={{ accentColor: 'var(--primary)', flexShrink: 0 }}
                />
                Show footer note
              </label>
            </OptionsSection>
          )}

                    {settings?.invoiceRjcCertDataUrl && (() => {
            const h = footImgDraft ?? settings.invoiceFooterImageHeight ?? FOOTER_IMG_H_DEFAULT;
            const shown = !memo.invoiceOptions?.hideFooterImage;
            return (
              <OptionsSection title="Footer Image" defaultOpen>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-1)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={shown}
                    onChange={e => updateMemoOption(memo.id, 'hideFooterImage', !e.target.checked)}
                    style={{ accentColor: 'var(--primary)', flexShrink: 0 }}
                  />
                  Show footer image
                </label>
                {shown && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Small</span>
                      <input
                        type="range" min={FOOTER_IMG_H_MIN} max={FOOTER_IMG_H_MAX} step={2} value={h}
                        onChange={e => setFootImgDraft(Number(e.target.value))}
                        aria-label="Footer image size"
                        style={{ flex: 1, accentColor: 'var(--primary)', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Big</span>
                    </div>
                    {h !== FOOTER_IMG_H_DEFAULT && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                        <button type="button" className="line-item-link-btn" style={{ fontSize: 11.5 }}
                          onClick={() => setFootImgDraft(FOOTER_IMG_H_DEFAULT)}>Reset</button>
                      </div>
                    )}
                  </>
                )}
              </OptionsSection>
            );
          })()}

          <OptionsSection title="Trade Details">
            <div className="invoice-trade-fields" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {INVOICE_TEXT_FIELDS.map(f => (
                <FormField key={f.key} label={f.label} className="invoice-trade-field">
                  <input
                    defaultValue={(memo.invoiceOptions?.[f.key] as string | undefined) ?? ''}
                    key={`${memo.id}-${f.key}`}
                    onBlur={e => updateMemoOption(memo.id, f.key, e.target.value.trim())}
                    placeholder="—"
                  />
                </FormField>
              ))}
            </div>
          </OptionsSection>

          <OptionsSection title="Customer Memo" defaultOpen>
            <div className="form-group invoice-trade-field">
              <textarea
                value={draftNotes}
                onChange={e => setDraftNotes(e.target.value)}
                onBlur={() => saveMemoNotes(memo.id)}
                placeholder="A note printed at the bottom of this consignment…"
              />
            </div>
          </OptionsSection>
        </div>
      </div>
    </Modal>
  );
}
